import api from './api';

// Device board catalog — one row per board generation. A device's board
// decides the capabilities[] and features{} the v2 customer API publishes for
// it, so this is the place a new hardware type is described, not code.
// One row of a board's append-only price list (board_prices). Amounts are
// minor units (cents). The row in force is the latest effective_from <= now.
export interface BoardPrice {
  id: number;
  board_id?: number;
  // SKU pricing (migration 055): rows with variant_id price one SKU; rows
  // without are the board-level family default an unpriced SKU inherits.
  variant_id?: number | null;
  inherited?: boolean;
  currency: string;
  hardware_price_cents: number;
  monthly_fee_cents: number;
  effective_from: string;
  note?: string | null;
  created_at?: string;
}

export interface BoardPriceInput {
  currency?: string;
  hardware_price_cents: number;
  monthly_fee_cents: number;
  effective_from?: string | null;
  note?: string | null;
}

export interface DeviceBoard {
  id: number;
  manufacturer: string;
  manufacturer_id: number | null;
  board: string;
  display_name: string | null;
  capabilities: string[];
  features: Record<string, boolean>;
  supported: Record<string, string[]>;
  default_num_doors: number;
  default_power_type: 'battery' | 'plugin';
  is_default: boolean;
  notes: string | null;
  device_count: number;
  current_price: BoardPrice | null;
  // Public posting state (board_listings); null until a listing row exists.
  listing_published: boolean | null;
  listing_slug: string | null;
  created_at: string;
  updated_at: string;
}

export interface BoardRegistry {
  capabilities: { slug: string; description: string }[];
  features: { slug: string; capability: string; description: string; gates: string }[];
  supported: { key: string; capability: string; values: string[]; operation: string; description: string }[];
  powerTypes: string[];
}

export type DeviceBoardInput = Partial<
  Omit<DeviceBoard, 'id' | 'device_count' | 'current_price' | 'listing_published' | 'listing_slug' | 'created_at' | 'updated_at'>
>;

export function boardLabel(board: Pick<DeviceBoard, 'manufacturer' | 'board' | 'display_name'>): string {
  return board.display_name || `${board.manufacturer} ${board.board}`;
}

/** 24900, 'USD' → "$249.00" (falls back to "249.00 XXX" for unknown codes). */
export function formatMoney(cents: number, currency = 'USD'): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

export async function getDeviceBoards(options: { manufacturerId?: number } = {}): Promise<DeviceBoard[]> {
  const params = options.manufacturerId ? `?manufacturerId=${options.manufacturerId}` : '';
  const response = await api.get<{ boards: DeviceBoard[] }>(`/device-boards${params}`);
  return response.data.boards;
}

export async function getDeviceBoard(id: number): Promise<DeviceBoard> {
  const response = await api.get<DeviceBoard>(`/device-boards/${id}`);
  return response.data;
}

export async function getBoardPrices(boardId: number): Promise<{ board_id: number; current: BoardPrice | null; prices: BoardPrice[] }> {
  const response = await api.get<{ board_id: number; current: BoardPrice | null; prices: BoardPrice[] }>(`/device-boards/${boardId}/prices`);
  return response.data;
}

export async function setBoardPrice(boardId: number, data: BoardPriceInput): Promise<BoardPrice> {
  const response = await api.post<BoardPrice>(`/device-boards/${boardId}/prices`, data);
  return response.data;
}

export async function getVariantPrices(boardId: number, variantId: number): Promise<{ board_id: number; variant_id: number; current: BoardPrice | null; prices: BoardPrice[] }> {
  const response = await api.get(`/device-boards/${boardId}/variants/${variantId}/prices`);
  return response.data;
}

export async function setVariantPrice(boardId: number, variantId: number, data: BoardPriceInput): Promise<BoardPrice> {
  const response = await api.post<BoardPrice>(`/device-boards/${boardId}/variants/${variantId}/prices`, data);
  return response.data;
}

export async function getBoardRegistry(): Promise<BoardRegistry> {
  const response = await api.get<BoardRegistry>('/device-boards/registry');
  return response.data;
}

export async function createDeviceBoard(data: DeviceBoardInput): Promise<DeviceBoard> {
  const response = await api.post<DeviceBoard>('/device-boards', data);
  return response.data;
}

export async function updateDeviceBoard(id: number, data: DeviceBoardInput): Promise<DeviceBoard> {
  const response = await api.put<DeviceBoard>(`/device-boards/${id}`, data);
  return response.data;
}

export async function deleteDeviceBoard(id: number): Promise<{ success: boolean }> {
  const response = await api.delete<{ success: boolean }>(`/device-boards/${id}`);
  return response.data;
}
